import { CLAIMS_REF } from '@backend/db/collections'
import { db } from '@backend/db/db'
import { useDocument } from '@backend/db/hooks/useDocument'
import {
  deleteEncounterClaimDraft,
  saveEncounterClaimDraft,
  submitEncounterClaim,
} from '@backend/functions/claims'
import { updateEncounterBillingFields } from '@backend/functions/gyn'
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  CircularProgress,
  Code,
  Divider,
  Flex,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useProfile } from '@contexts/user/ProfileContext'
import {
  PopUpMessageContext,
  ScreenContext,
  ThemeContext,
  useApp,
  usePopUpMessage,
} from '@hb/react'
import {
  CLAIMS,
  CollectionItem,
  colors,
  defaultStageValidate,
  dosColorScheme,
  Encounter,
  EncounterType,
  FacilityClaim,
  FieldMap,
  FieldMapValue,
  getDaysSinceDOS,
  INSURANCE_PROVIDERS,
  InsuranceProvider,
  isSupplyLogReviewComplete,
  objectToArray,
  PregnancyClaim,
  SubmitEncounterClaimArgs,
  version,
  WithId,
} from '@hb/shared'
import { useQuery } from '@hooks/backend/useQuery'
import { FORM_ERROR, ValidationErrors } from 'final-form'
import arrayMutators from 'final-form-arrays'
import { collection, CollectionReference, deleteDoc, doc, query, where } from 'firebase/firestore'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import { DeleteButton } from '../../../components/Buttons'
import { FormElement } from '../../../components/forms/Input'
import { DefaultModal } from '../../../components/Modals/DefaultModal'
import { AbilityClaimView } from '../../../components/Users/Profile/Claims/Ability/AbilityClaim'
import {
  abilityClaimField,
  customRelateCodePrimary,
  customRelateCodeSecondary,
  diagnosisCodesField,
  servicesValidationField,
  shouldUseCustomRelateCodeField,
  shouldUseCustomRenderingProviderField,
} from '../../../components/Users/Profile/Claims/Ability/fields'
import { InovalonButton } from '../../../components/Users/Profile/Claims/Ability/InovalonButton'
import { ClaimSubmissionForm } from '../../../components/Users/Profile/Claims/Ability/SubmitNewClaim/ClaimSubmissionForm'
import { DiagnosisCodesView } from '../../../components/Users/Profile/Claims/Ability/SubmitNewClaim/DiagnosisCodes'
import { SaveDraftModal } from '../../../components/Users/Profile/Claims/Ability/SubmitNewClaim/SaveDraftModal'
import { SelectInsurer } from '../../../components/Users/Profile/Claims/Ability/SubmitNewClaim/SelectClaimInsurer'
import { SelectDraftMenu } from '../../../components/Users/Profile/Claims/Ability/SubmitNewClaim/SelectDraftMenu'
import { Services } from '../../../components/Users/Profile/Claims/Ability/SubmitNewClaim/Services'
import { ClaimView } from '../../../components/Users/Profile/Claims/ClaimView'

// ─── Firestore ref typed for FacilityClaim ────────────────────────────────────
const FACILITY_CLAIMS_REF = collection(db, CLAIMS) as CollectionReference<
  CollectionItem<FacilityClaim>
>

// ─── Query hook ───────────────────────────────────────────────────────────────
const useEncounterClaims = (encounterId: string) => {
  const ref = useMemo(
    () => query(FACILITY_CLAIMS_REF, where('encounterId', '==', encounterId)),
    [encounterId],
  )
  return useQuery<FacilityClaim>(ref)
}

// ─── Validation field ─────────────────────────────────────────────────────────
const encounterValidationField: FieldMap = {
  name: 'New encounter claim',
  children: {
    ...abilityClaimField.children,
    DiagnosisCodes: diagnosisCodesField,
    Services: servicesValidationField,
    shouldUseCustomRelateCodePrimary: shouldUseCustomRelateCodeField,
    customRelateCodePrimary,
    shouldUseCustomRelateCodeSecondary: shouldUseCustomRelateCodeField,
    customRelateCodeSecondary,
    shouldUseCustomRenderingProvider: shouldUseCustomRenderingProviderField,
  },
}

// ─── Form data transform ──────────────────────────────────────────────────────
const formDiagnosisCodesToAbility = (data?: Array<FieldMapValue>) =>
  (data && Array.isArray(data) ? data : []).map(({ diagnosisCodeSelect, customDiagnosisCode }) =>
    diagnosisCodeSelect === 'other' ? customDiagnosisCode : diagnosisCodeSelect,
  )

const formDataToEncounterClaimData = (
  data: FieldMapValue,
  encounterId: string,
  draftId?: string | null,
): SubmitEncounterClaimArgs => {
  const { DiagnosisCodes, ...rest } = data
  const submitted: FieldMapValue = {
    encounterId,
    DiagnosisCodes: formDiagnosisCodesToAbility(DiagnosisCodes),
    ...rest,
  }
  if (draftId) submitted.draftId = draftId
  return submitted as SubmitEncounterClaimArgs
}

// ─── Claim row ────────────────────────────────────────────────────────────────
const EncounterClaimRow = ({
  claim,
  onClick,
}: {
  claim: WithId<FacilityClaim>
  onClick: () => void
}) => (
  <ClaimView id={claim.id} claim={claim as unknown as WithId<PregnancyClaim>} onClick={onClick} />
)

// ─── Claim detail modal ───────────────────────────────────────────────────────
const EncounterClaimDetailModal = ({
  claim,
  onClose,
}: {
  claim: WithId<FacilityClaim> | null
  onClose: () => void
}) => {
  const submittedToAbility = useMemo(() => claim?.submittedToAbility, [claim])
  const { processResponse } = usePopUpMessage()
  return (
    <DefaultModal
      size="4xl"
      bodyProps={{ minHeight: '440px' }}
      contentProps={{ bg: 'gray.50' }}
      overlayHeader
      isOpen={!!claim}
      onClose={onClose}
      render={() =>
        !claim ? (
          <></>
        ) : (
          <Tabs h="100%" isLazy defaultIndex={claim.abilityId ? 0 : 1}>
            <TabList bg="white" pt={1}>
              <Tab>CLAIM</Tab>
              {claim.submittedArgs ? <Tab>SUBMISSION FORM</Tab> : null}
              {submittedToAbility ? <Tab>SUBMITTED TO ABILITY</Tab> : null}
              <Tab>DELETE CLAIM</Tab>
            </TabList>
            <TabPanels>
              <TabPanel p={3}>
                <AbilityClaimView
                  withMore
                  claimId={claim.id}
                  claim={claim as unknown as WithId<PregnancyClaim>}
                />
              </TabPanel>
              {claim.submittedArgs ? (
                <TabPanel p={0}>
                  <ClaimSubmissionForm claim={claim as unknown as PregnancyClaim} />
                </TabPanel>
              ) : null}
              {submittedToAbility ? (
                <TabPanel p={0}>
                  <Code minW="100%" p={4} whiteSpace="pre-wrap">
                    {JSON.stringify(submittedToAbility, null, 2)}
                  </Code>
                </TabPanel>
              ) : null}
              <TabPanel>
                <Flex w="100%" flexFlow="column" gap={1}>
                  <Text px={2} fontSize="lg" color="gray.600">
                    This will delete the claim from the Hamilton Billing system, but not from
                    Inovalon
                  </Text>
                  <DeleteButton
                    itemName="claim"
                    text="DELETE CLAIM"
                    size="md"
                    onDelete={async () => {
                      try {
                        await deleteDoc(doc(CLAIMS_REF, claim.id))
                        onClose()
                      } catch (err: any) {
                        processResponse({ error: `Error deleting claim: ${err.message}` })
                      }
                    }}
                  />
                </Flex>
              </TabPanel>
            </TabPanels>
          </Tabs>
        )
      }
    />
  )
}

// ─── Encounter claim submission form ─────────────────────────────────────────
export const EncounterClaimSubmission = ({
  encounterId,
  encounter,
  onClose,
  existingAbilityId,
}: {
  encounterId: string
  encounter: Encounter
  onClose: () => void
  existingAbilityId?: string | null
}) => {
  const {
    selectedAssessment: { populated: selectedAssessment },
    user,
  } = useProfile()

  const { claimsDrafts } = encounter

  const { showError } = useContext(PopUpMessageContext)

  const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null)
  const [savingAsDraft, setSavingAsDraft] = useState<{
    id: string
    data: FieldMapValue
  } | null>(null)

  const onSaveDraftComplete = useCallback((id: string) => {
    setSavingAsDraft(null)
    setSelectedDraftId(id)
  }, [])

  const onSaveDraftClick = useCallback(
    async (data: FieldMapValue) => {
      const id = selectedDraftId ?? `${Date.now()}`
      setSavingAsDraft({ id, data })
      return undefined
    },
    [selectedDraftId],
  )

  const onSaveDraft = useCallback(
    async (id: string, name: string, data: FieldMapValue) => {
      try {
        const result = await saveEncounterClaimDraft({ encounterId, draftId: id, name, data })
        return result.data
      } catch (err: any) {
        return { error: err.message ?? 'Error saving draft' }
      }
    },
    [encounterId],
  )

  const onDeleteDraft = useCallback(
    async (id: string) => {
      await deleteEncounterClaimDraft({ encounterId, draftId: id })
    },
    [encounterId],
  )

  const existingDraftNames = useMemo(
    () => Object.values(claimsDrafts ?? {}).map(d => d.name),
    [claimsDrafts],
  )

  const onSubmit = useCallback(
    async (data: FieldMapValue): Promise<ValidationErrors> => {
      const submitted = formDataToEncounterClaimData(data, encounterId, selectedDraftId)
      if (existingAbilityId) submitted.claimId = existingAbilityId
      if (version === 'dev') submitted.testMode = true
      return submitEncounterClaim(submitted)
        .then(() => {
          onClose()
          return { success: version === 'dev' ? 'Test claim submitted' : 'Claim submitted' }
        })
        .catch((err: any) => {
          console.error(err)
          const msg = err.message
          showError(msg)
          return { [FORM_ERROR]: msg }
        })
    },
    [encounterId, showError, onClose, selectedDraftId, existingAbilityId],
  )

  const initialMainCoverageId = useMemo(
    () => selectedAssessment?.plans?.primary?.id ?? null,
    [selectedAssessment],
  )

  const defaultInitValue = useMemo(
    () => ({
      mainCoverageId: initialMainCoverageId,
      dischargeDateSameAsAdmission: true,
    }),
    [initialMainCoverageId],
  )

  const [initValue, setInitValue] = useState<FieldMapValue>(
    selectedDraftId
      ? ((claimsDrafts?.[selectedDraftId]?.data as FieldMapValue) ?? {})
      : defaultInitValue,
  )
  const [reinitializing, setReinitializing] = useState(false)

  useEffect(() => {
    if (selectedDraftId) {
      if (claimsDrafts?.[selectedDraftId]?.data) {
        setReinitializing(true)
        setTimeout(() => {
          const init = (claimsDrafts?.[selectedDraftId]?.data ?? {}) as FieldMapValue
          const { Services: initServices } = init
          setInitValue({
            ...init,
            Services: initServices?.map((s: FieldMapValue) => ({
              ...s,
              DiagnosisPointer:
                typeof s.DiagnosisPointer === 'string' ? [s.DiagnosisPointer] : s.DiagnosisPointer,
            })),
          })
          setReinitializing(false)
        }, 200)
      }
    } else {
      setReinitializing(true)
      setTimeout(() => {
        setInitValue(defaultInitValue)
        setReinitializing(false)
      }, 200)
    }
  }, [selectedDraftId, claimsDrafts, defaultInitValue])

  const validate = useMemo(() => defaultStageValidate(encounterValidationField), [])

  return (
    <>
      <ThemeContext.Provider value={{ theme: 'detailed' }}>
        <VStack w="100%" spacing={2} align="flex-start" px={4} py={3}>
          {reinitializing ? (
            <Center w="100%" minH="500px">
              <CircularProgress color="green.400" isIndeterminate />
            </Center>
          ) : (
            <Form
              onSubmit={onSubmit}
              initialValues={initValue}
              keepDirtyOnReinitialize={true}
              validate={validate}
              mutators={{ ...(arrayMutators as any) }}>
              {({ handleSubmit, submitting, hasValidationErrors }) => (
                <>
                  <Flex px={2} align="center" w="100%">
                    <Text color="gray.500" fontSize="lg">
                      {existingAbilityId ? 'RE-SUBMIT CLAIM' : 'SUBMIT NEW CLAIM'}
                    </Text>
                    <HStack flex={1} justify="center" position="relative">
                      <SelectDraftMenu
                        onSaveDraftClick={onSaveDraftClick}
                        selectedDraftId={selectedDraftId}
                        onSelect={setSelectedDraftId}
                        drafts={
                          claimsDrafts as
                            | Record<string, { name: string; data: FieldMapValue }>
                            | undefined
                        }
                        onDeleteDraft={onDeleteDraft}
                      />
                    </HStack>
                    <HStack justify="flex-end" align="center">
                      <Button
                        size="sm"
                        boxShadow="md"
                        onClick={onClose}
                        bg="white"
                        color={colors.green.hex}>
                        CANCEL
                      </Button>
                      <Button
                        size="sm"
                        boxShadow="md"
                        isLoading={submitting}
                        onClick={handleSubmit}
                        bg={hasValidationErrors ? 'gray.300' : colors.green.hex}
                        color="white">
                        {existingAbilityId ? 'RE-SUBMIT' : 'SUBMIT'}
                      </Button>
                    </HStack>
                  </Flex>
                  <VStack w="100%">
                    <VStack w="100%">
                      <VStack
                        borderRadius={4}
                        border="1px solid #cdcdcd"
                        bg="white"
                        boxShadow="md"
                        spacing={0}
                        width="100%"
                        align="flex-start"
                        flex={1}
                        px={4}
                        py={2}>
                        <FormElement root active name="" field={abilityClaimField} />
                        <Services />
                        <DiagnosisCodesView />
                      </VStack>
                      <SelectInsurer assessment={selectedAssessment ?? null} user={user ?? null} />
                    </VStack>
                  </VStack>
                </>
              )}
            </Form>
          )}
        </VStack>
      </ThemeContext.Provider>
      <SaveDraftModal
        savingAsDraft={savingAsDraft}
        onClose={() => setSavingAsDraft(null)}
        onComplete={onSaveDraftComplete}
        onSave={onSaveDraft}
        existingDraftNames={existingDraftNames}
      />
    </>
  )
}

// ─── Encounter claim submission modal ─────────────────────────────────────────
const EncounterClaimSubmissionModal = ({
  isOpen,
  onClose,
  encounterId,
  encounter,
}: {
  isOpen: boolean
  onClose: () => void
  encounterId: string
  encounter: Encounter
}) => {
  const screen = useContext(ScreenContext)
  return (
    <DefaultModal
      closeOnOverlayClick={false}
      closeOnEsc={false}
      overlayHeader
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      scrollBehavior="inside"
      bodyProps={{ maxH: screen.height, pt: 0 }}
      contentProps={{ pt: 0 }}
      render={() =>
        isOpen ? (
          <EncounterClaimSubmission
            encounterId={encounterId}
            encounter={encounter}
            onClose={onClose}
          />
        ) : (
          <HStack p={3}>
            <CircularProgress size={5} color="gray" isIndeterminate />
            <Text>Loading...</Text>
          </HStack>
        )
      }
    />
  )
}

// getDaysSinceDOS and dosColorScheme imported from @hb/shared

// const authColorScheme: Record<EncounterStatus, string> = {
//   [EncounterStatus.DRAFT]: 'orange',
//   [EncounterStatus.COMPLETED]: 'green',
//   [EncounterStatus.AUTHENTICATED]: 'blue',
// }

const isBirthingCenterEncounter = (encounter: Encounter): boolean => {
  if (encounter.type === EncounterType.BIRTHING_CENTER_ADMISSION) return true
  const data = encounter.data as { visitLocation?: { location?: string } } | undefined
  return data?.visitLocation?.location === 'birthing-center'
}

// ─── Birthing center banner ───────────────────────────────────────────────────
const BirthingCenterBanner = () => (
  <Alert status="warning" borderRadius={4} fontSize="sm" mb={2}>
    <AlertIcon />
    This encounter occurred at a birthing center. A facility claim may be required in addition to
    this professional claim. Confirm facility billing has been initiated before submitting the
    professional claim.
  </Alert>
)

// ─── Facility claim checklist ─────────────────────────────────────────────────
const CHECKLIST_ITEMS = [
  'Facility claim created and linked',
  'Supply log reviewed and revenue codes assigned',
  'Facility claim payer requirements confirmed',
  'Both claims linked under same encounter date and episode',
] as const

// Index of checklist items that are auto-computed rather than manually checked.
// Item 1 = "Supply log reviewed and revenue codes assigned" — driven by billingSupplyAssignments.
// const AUTO_COMPUTED_CHECKLIST_INDICES = new Set([1])

const FacilityClaimChecklist = ({
  checked,
  onChange,
  payerRequirements = [],
  requirementChecks = [],
  onRequirementChange,
}: {
  checked: boolean[]
  onChange: (index: number, value: boolean) => void
  payerRequirements?: string[]
  requirementChecks?: boolean[]
  onRequirementChange?: (index: number, value: boolean) => void
}) => {
  const hasPayerRequirements = payerRequirements.length > 0
  return (
    <VStack
      align="flex-start"
      spacing={1}
      px={3}
      py={2}
      borderRadius={4}
      border="1px solid #e2c97e"
      bg="yellow.50"
      w="100%"
      mb={2}>
      <Text fontSize="xs" fontWeight={600} color="yellow.800">
        BIRTHING CENTER CHECKLIST — required before creating a claim
      </Text>
      {CHECKLIST_ITEMS.map((label, i) => {
        // Item 1 is always auto (supply log). Item 2 is auto when payer requirements are configured.
        const isAuto = i === 1 || (i === 2 && hasPayerRequirements)
        return (
          <Box key={i} w="100%">
            <Checkbox
              isChecked={checked[i]}
              onChange={isAuto ? undefined : e => onChange(i, e.target.checked)}
              isReadOnly={isAuto}
              colorScheme="green"
              size="sm">
              <Text fontSize="sm" color="gray.700">
                {label}
                {isAuto ? (
                  <Text as="span" fontSize="xs" color="gray.400" ml={1}>
                    (auto)
                  </Text>
                ) : null}
              </Text>
            </Checkbox>
            {/* §6.6: Dynamic sub-checklist for payer requirements when configured in insurer profile. */}
            {i === 2 && hasPayerRequirements && (
              <VStack align="flex-start" spacing={0.5} pl={6} pt={1} w="100%">
                <Text fontSize="xs" color="gray.500" fontWeight={500} mb={0.5}>
                  Payer requirements:
                </Text>
                {payerRequirements.map((req, ri) => (
                  <Checkbox
                    key={ri}
                    isChecked={requirementChecks[ri] ?? false}
                    onChange={e => onRequirementChange?.(ri, e.target.checked)}
                    colorScheme="green"
                    size="sm">
                    <Text fontSize="xs" color="gray.700" pt={0.5}>
                      {req}
                    </Text>
                  </Checkbox>
                ))}
              </VStack>
            )}
          </Box>
        )
      })}
    </VStack>
  )
}

// ─── Main section ─────────────────────────────────────────────────────────────
export const EncounterClaimsSection = ({
  encounter,
  examId,
}: {
  encounter: Encounter
  examId: string
}) => {
  const {
    selectedAssessment: { populated: selectedAssessment },
  } = useProfile()
  const { appName } = useApp()
  const insurerId = useMemo(() => selectedAssessment?.insurerId ?? null, [selectedAssessment])
  const { data: insurer } = useDocument<InsuranceProvider>(INSURANCE_PROVIDERS, insurerId)
  const timelyFilingDays = insurer?.timelyFilingDays ?? 90

  const { data: claims } = useEncounterClaims(examId)
  const claimsArr = useMemo(() => objectToArray<FacilityClaim>(claims ?? {}), [claims])
  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null)
  const [creatingClaim, setCreatingClaim] = useState(false)
  const [checklistState, setChecklistState] = useState<boolean[]>(
    encounter.billingChecklist ?? Array(CHECKLIST_ITEMS.length).fill(false),
  )

  const selectedClaim = useMemo(
    () => (selectedClaimId ? (claimsArr.find(c => c.id === selectedClaimId) ?? null) : null),
    [selectedClaimId, claimsArr],
  )

  const isBirthingCenter = useMemo(() => isBirthingCenterEncounter(encounter), [encounter])

  // §6.6: Resolve payer-specific requirements for the facility claim checklist item 3.
  // Mirrors the requirementsByCodeType lookup used in ClaimBillingActions.
  const facilityPayerRequirements = useMemo<string[]>(() => {
    if (!insurer?.requirementsByCodeType) return []
    const firstCpt = encounter.cptCodes?.[0] ?? null
    if (!firstCpt) return []
    return insurer.requirementsByCodeType.find(r => r.cptCode === firstCpt)?.requirements ?? []
  }, [insurer, encounter.cptCodes])

  const [facilityRequirementChecks, setFacilityRequirementChecks] = useState<boolean[]>(() =>
    Array(facilityPayerRequirements.length).fill(false),
  )

  // Reset sub-requirement checks when the requirements list changes (e.g. payer change).
  useEffect(() => {
    setFacilityRequirementChecks(Array(facilityPayerRequirements.length).fill(false))
  }, [facilityPayerRequirements.length])

  const handleRequirementChange = useCallback((index: number, value: boolean) => {
    setFacilityRequirementChecks(prev => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }, [])

  // Item 1 ("Supply log reviewed and revenue codes assigned") is auto-computed
  // from billingSupplyAssignments. Item 2 ("Payer requirements confirmed") is auto-computed
  // when payer requirements are configured.
  const supplyLogComplete = useMemo(() => isSupplyLogReviewComplete(encounter), [encounter])
  const facilityPayerRequirementsComplete = useMemo(
    () =>
      facilityPayerRequirements.length === 0 ||
      facilityRequirementChecks.slice(0, facilityPayerRequirements.length).every(Boolean),
    [facilityPayerRequirements, facilityRequirementChecks],
  )
  const effectiveChecklistState = useMemo(() => {
    const state = [...checklistState]
    state[1] = supplyLogComplete
    if (facilityPayerRequirements.length > 0) {
      state[2] = facilityPayerRequirementsComplete
    }
    return state
  }, [
    checklistState,
    supplyLogComplete,
    facilityPayerRequirements.length,
    facilityPayerRequirementsComplete,
  ])

  const checklistComplete = useMemo(
    () => effectiveChecklistState.every(Boolean),
    [effectiveChecklistState],
  )

  const daysSinceDOS = useMemo(() => getDaysSinceDOS(encounter.date), [encounter.date])
  const daysRemaining = daysSinceDOS !== null ? timelyFilingDays - daysSinceDOS : null

  const deliveredOn = selectedAssessment?.deliveredOn ?? null
  const daysSinceDelivery = useMemo(() => {
    if (!deliveredOn) return null
    const diffMs = Date.now() - deliveredOn
    return Math.floor(diffMs / 86_400_000)
  }, [deliveredOn])
  const deliveryClaimDaysRemaining = daysSinceDelivery !== null ? 15 - daysSinceDelivery : null

  const handleChecklistChange = useCallback(
    (index: number, value: boolean) => {
      setChecklistState(prev => {
        const next = [...prev]
        next[index] = value
        // Firestore rules allow no client writes to ENCOUNTERS — persist via callable.
        updateEncounterBillingFields({
          encounterId: examId,
          billingChecklist: next,
          appName,
        }).catch(console.error)
        return next
      })
    },
    [examId, appName],
  )

  return (
    <>
      <VStack w="100%" align="flex-start" spacing={0}>
        {/* Header row */}
        <HStack w="100%" px={2} py={1} justify="space-between" align="center">
          <HStack flex={1} minW={0} justify="space-between" spacing={2} align="center">
            <Text fontWeight={600} color="gray.600" fontSize="sm">
              CLAIMS
            </Text>
            {daysSinceDOS !== null ? (
              <Badge px={2} colorScheme={dosColorScheme(daysSinceDOS)} fontSize="sm">
                {daysSinceDOS} days since DOS
              </Badge>
            ) : null}
          </HStack>
          <InovalonButton
            isDisabled={isBirthingCenter && !checklistComplete}
            onClick={() => setCreatingClaim(true)}>
            + CREATE CLAIM
          </InovalonButton>
        </HStack>

        {/* Birthing center banner + checklist */}
        {isBirthingCenter ? (
          <VStack w="100%" px={2} pb={1} spacing={0} align="flex-start">
            <BirthingCenterBanner />
            <FacilityClaimChecklist
              checked={effectiveChecklistState}
              onChange={handleChecklistChange}
              payerRequirements={facilityPayerRequirements}
              requirementChecks={facilityRequirementChecks}
              onRequirementChange={handleRequirementChange}
            />
          </VStack>
        ) : null}

        {/* Delivery claim urgency alert */}
        {deliveryClaimDaysRemaining !== null ? (
          <Alert
            status={deliveryClaimDaysRemaining <= 0 ? 'error' : 'warning'}
            borderRadius={4}
            fontSize="sm"
            mb={1}
            mx={2}
            w="calc(100% - 1rem)">
            <AlertIcon />
            {deliveryClaimDaysRemaining <= 0
              ? `Delivery claim deadline passed — submit immediately (${Math.abs(deliveryClaimDaysRemaining)} day${Math.abs(deliveryClaimDaysRemaining) === 1 ? '' : 's'} overdue).`
              : `Delivery documented. Submit delivery claim within ${deliveryClaimDaysRemaining} day${deliveryClaimDaysRemaining === 1 ? '' : 's'} (15-day deadline from delivery date).`}
          </Alert>
        ) : null}

        {/* Timely filing warning */}
        {daysRemaining !== null && daysRemaining <= 25 ? (
          <HStack px={2} pb={1}>
            <Text fontSize="xs" color={daysRemaining <= 0 ? 'red.700' : 'red.600'} fontWeight={500}>
              {daysRemaining <= 0
                ? `⚠ Timely filing deadline passed — ${Math.abs(daysRemaining)} days overdue`
                : `⚠ Timely filing at risk — ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining`}
            </Text>
          </HStack>
        ) : null}

        {/* Claims list */}
        {claimsArr.length > 0 ? (
          <>
            <Divider />
            <VStack w="100%" spacing={1} px={2} py={2}>
              {claimsArr.map(claim => (
                <EncounterClaimRow
                  key={claim.id}
                  claim={claim}
                  onClick={() => setSelectedClaimId(claim.id)}
                />
              ))}
            </VStack>
          </>
        ) : (
          <Text px={2} pb={2} color="gray.400" fontSize="md">
            {isBirthingCenter
              ? 'No professional claim submitted. For birthing center encounters, coordinate professional and facility claim submission.'
              : 'No claims submitted'}
          </Text>
        )}
      </VStack>
      <EncounterClaimDetailModal claim={selectedClaim} onClose={() => setSelectedClaimId(null)} />
      <EncounterClaimSubmissionModal
        isOpen={creatingClaim}
        onClose={() => setCreatingClaim(false)}
        encounterId={examId}
        encounter={encounter}
      />
    </>
  )
}
